package info.rezident.app.data.admin

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.FieldPath
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.firestore
import info.rezident.app.core.Logger
import info.rezident.app.data.quiz.EmailTemplates
import info.rezident.app.data.quiz.QuizQuestionModel
import kotlinx.coroutines.*
import kotlin.js.Date

/**
 * Administrative operations that should only be triggered by an authorized admin user from the Admin page.
 */
object AdminOperationsRepository {
    private val firestore: FirebaseFirestore = Firebase.firestore

    private fun currentTime(): String {
        return Date().toISOString()
    }

    data class AdminEmailsResult(
        val waitlistEmails: List<String>,
        val userEmails: List<String>,
        val uniqueEmails: List<String>,
        val waitlistOnlyEmails: List<String>
    )

    data class CheckoutPaymentCountByAcademicYear(
        val academicYear: String,
        val checkoutStarts: Int,
        val successfulPayments: Int
    )

    data class CheckoutPaymentCountResult(
        val countsByAcademicYear: List<CheckoutPaymentCountByAcademicYear>,
        val usersInDateRange: Int,
        val usersWithCheckoutSession: Int,
        val usersWithSuccessfulPayment: Int,
        val errors: Int
    )

    private data class UserCheckoutPaymentStatus(
        val userId: String,
        val academicYear: String?,
        val hasCheckoutSession: Boolean,
        val hasSuccessfulPayment: Boolean,
        val errorMessage: String? = null
    )

    /**
     * Counts users created from November 1, 2025 through the current time who have a stripeId, grouped by
     * academicYear with separate columns for checkout starts and successful payments.
     */
    suspend fun countUsersSinceNovember2025WithCheckoutAndPaymentByAcademicYear(
        onProgress: (String) -> Unit = { message -> Logger.log(message) }
    ): CheckoutPaymentCountResult = withContext(Dispatchers.Default) {
        val startDateCreatedInclusive = "2025-11-01T00:00:00.000Z"
        val endDateCreatedInclusive = currentTime()

        onProgress(
            "[Admin] Starting checkout/payment count for users created from $startDateCreatedInclusive to $endDateCreatedInclusive"
        )

        val countsByAcademicYear = mutableMapOf<String, CheckoutPaymentCountByAcademicYear>()
        var usersInDateRange = 0
        var usersChecked = 0
        var usersWithCheckoutSession = 0
        var usersWithSuccessfulPayment = 0
        var errors = 0

        try {
            val usersSnapshot = firestore.collection("users")
                .where {
                    all(
                        "dateCreated" greaterThanOrEqualTo startDateCreatedInclusive,
                        "dateCreated" lessThanOrEqualTo endDateCreatedInclusive,
                        "stripeId" greaterThan ""
                    )
                }
                .orderBy("dateCreated")
                .orderBy("stripeId")
                .get()
            onProgress("[Admin] Loaded ${usersSnapshot.documents.size} users in dateCreated range with stripeId")
            usersInDateRange = usersSnapshot.documents.size

            for (userChunk in usersSnapshot.documents.chunked(10)) {
                val firstUserNumber = usersChecked + 1
                val lastUserNumber = usersChecked + userChunk.size
                onProgress("[Admin] Checking users $firstUserNumber-$lastUserNumber/$usersInDateRange")

                val statuses = coroutineScope {
                    userChunk.map { userDoc ->
                        async {
                            try {
                                val checkoutSessionsSnapshot = userDoc.reference.collection("checkout_sessions")
                                    .limit(1)
                                    .get()
                                val hasCheckoutSession = checkoutSessionsSnapshot.documents.isNotEmpty()

                                if (!hasCheckoutSession) {
                                    return@async UserCheckoutPaymentStatus(
                                        userId = userDoc.id,
                                        academicYear = null,
                                        hasCheckoutSession = false,
                                        hasSuccessfulPayment = false
                                    )
                                }

                                val academicYear: String? = if (userDoc.contains("academicYear")) {
                                    runCatching { userDoc.get<String>("academicYear") }.getOrNull()
                                        ?.trim()
                                        ?.takeIf { it.isNotEmpty() }
                                } else {
                                    null
                                }

                                val paymentsSnapshot = userDoc.reference.collection("payments")
                                    .where { "status" equalTo "succeeded" }
                                    .limit(1)
                                    .get()
                                val hasSuccessfulPayment = paymentsSnapshot.documents.isNotEmpty()

                                UserCheckoutPaymentStatus(
                                    userId = userDoc.id,
                                    academicYear = academicYear,
                                    hasCheckoutSession = true,
                                    hasSuccessfulPayment = hasSuccessfulPayment
                                )
                            } catch (perUserError: Throwable) {
                                UserCheckoutPaymentStatus(
                                    userId = userDoc.id,
                                    academicYear = null,
                                    hasCheckoutSession = false,
                                    hasSuccessfulPayment = false,
                                    errorMessage = perUserError.message
                                )
                            }
                        }
                    }.awaitAll()
                }

                for (status in statuses) {
                    if (status.errorMessage != null) {
                        errors++
                        val msg = "[Admin] Failed processing user ${status.userId}: ${status.errorMessage}"
                        Logger.error(msg)
                        onProgress(msg)
                        continue
                    }

                    if (status.hasCheckoutSession) {
                        usersWithCheckoutSession++
                        val academicYear = status.academicYear ?: "Unknown"
                        val currentCount = countsByAcademicYear[academicYear]
                            ?: CheckoutPaymentCountByAcademicYear(
                                academicYear = academicYear,
                                checkoutStarts = 0,
                                successfulPayments = 0
                            )
                        countsByAcademicYear[academicYear] = currentCount.copy(
                            checkoutStarts = currentCount.checkoutStarts + 1,
                            successfulPayments = currentCount.successfulPayments +
                                    if (status.hasSuccessfulPayment) 1 else 0
                        )
                    }

                    if (status.hasSuccessfulPayment) {
                        usersWithSuccessfulPayment++
                    }
                }

                usersChecked += userChunk.size
                onProgress(
                    "[Admin] Checked $usersChecked/$usersInDateRange users; checkout=$usersWithCheckoutSession; successfulPayment=$usersWithSuccessfulPayment; errors=$errors"
                )
            }
        } catch (e: Throwable) {
            errors++
            val msg = "[Admin] Failed counting users created since November 2025: ${e.message}"
            Logger.error(msg)
            onProgress(msg)
        }

        val rows = countsByAcademicYear
            .values
            .sortedWith(compareBy<CheckoutPaymentCountByAcademicYear> { it.academicYear == "Unknown" }
                .thenBy { it.academicYear })

        onProgress(
            "[Admin] Finished. usersInDateRange=$usersInDateRange, checkout=$usersWithCheckoutSession, successfulPayment=$usersWithSuccessfulPayment, errors=$errors"
        )

        CheckoutPaymentCountResult(
            countsByAcademicYear = rows,
            usersInDateRange = usersInDateRange,
            usersWithCheckoutSession = usersWithCheckoutSession,
            usersWithSuccessfulPayment = usersWithSuccessfulPayment,
            errors = errors
        )
    }

    /**
     * Iterates through all documents in "waitlistMvp" and "users" collections, extracts the "email" field,
     * prints counts and email lists to console, and returns the values, also computing a unique combined list.
     */
    suspend fun collectAllEmails(): AdminEmailsResult = withContext(Dispatchers.Default) {
        val waitlistEmails: List<String> = try {
            val waitlistSnapshot = firestore.collection("waitlistMvp").get()
            waitlistSnapshot.documents.mapNotNull { doc ->
                val raw: String? = try {
                    doc.get("e-mail")
                } catch (_: Throwable) {
                    null
                }
                raw?.trim()?.lowercase()?.takeIf { it.isNotEmpty() }
            }
        } catch (e: Exception) {
            Logger.error("[Admin] Failed to read waitlistMvp emails: $e")
            emptyList()
        }

        val userEmails: List<String> = try {
            val usersSnapshot = firestore.collection("users").get()
            usersSnapshot.documents.mapNotNull { doc ->
                val raw: String? = try {
                    doc.get("email")
                } catch (_: Throwable) {
                    null
                }
                raw?.trim()?.lowercase()?.takeIf { it.isNotEmpty() }
            }
        } catch (e: Exception) {
            Logger.error("[Admin] Failed to read users emails: $e")
            emptyList()
        }

        Logger.debug("[Admin] waitlistMvp emails count: ${waitlistEmails.size}")
        Logger.debug("[Admin] waitlistMvp emails: ${waitlistEmails.joinToString(", ")}")

        Logger.debug("[Admin] users emails count: ${userEmails.size}")
        Logger.debug("[Admin] users emails: ${userEmails.joinToString(", ")}")

        val uniqueEmails: List<String> = (waitlistEmails + userEmails).toSet().toList().sorted()
        Logger.debug("[Admin] unique emails count: ${uniqueEmails.size}")
        Logger.debug("[Admin] unique emails: ${uniqueEmails.joinToString(", ")}")

        // Find waitlist emails that are not in user emails
        val waitlistOnlyEmails: List<String> = waitlistEmails.filter { it !in userEmails }
        Logger.debug("[Admin] waitlist-only emails count: ${waitlistOnlyEmails.size} out of ${waitlistEmails.size} total waitlist emails")
        Logger.debug("[Admin] waitlist-only emails: ${waitlistOnlyEmails.joinToString(", ")}")

        AdminEmailsResult(
            waitlistEmails = waitlistEmails,
            userEmails = userEmails,
            uniqueEmails = uniqueEmails,
            waitlistOnlyEmails = waitlistOnlyEmails
        )
    }

    /**
     * Iterate through all users and all their quizzes, and set two fields on each quiz document:
     * - templateId = 1
     * - quizId = <quiz document id>
     * Uses merge updates to avoid overwriting other fields. Returns a summary string.
     */
    suspend fun backfillTemplateAndQuizIdForAllQuizzes(
        onLog: (String) -> Unit = { message -> Logger.log(message) }
    ): String = withContext(Dispatchers.Default) {
        onLog("[Admin] Starting backfill (templateId & quizId) at ${currentTime()}")

        var usersProcessed: Int = 0
        var quizzesVisited: Int = 0
        var quizzesUpdated: Int = 0
        var failures: Int = 0

        try {
            val users = listOf(
                "HyExzQyPxfPk53XvuleMSy2h1Wl2",
                "E30cJxB9nwXnZvlWIUXmDQic2Br1",
                "pzYg0Gdg25OAQ1zTKKC21RqGxo32",
                "UdbltDCdGcWR7Pa35qoF0J0xuSh2",
                "PoPEzuRjPRc6dUNAhHGDBLZsUYP2"
            )

            val usersSnap = firestore.collection("users")
                .where {
                    FieldPath.documentId.equalTo("PoPEzuRjPRc6dUNAhHGDBLZsUYP2")
                }
                .get()
            onLog("[Admin] Found ${usersSnap.documents.size} users. Processing quizzes...")


            for (userDoc in usersSnap.documents) {
                usersProcessed++
                try {
                    val quizzesSnap = userDoc.reference.collection("quizzes").get()
                    val quizzesCount = quizzesSnap.documents.size
                    onLog("[${usersProcessed}] User ${userDoc.id}: found ${quizzesCount} quizzes")

                    for (quizDoc in quizzesSnap.documents) {
                        quizzesVisited++
                        val qid: String = quizDoc.id
                        try {
                            val update = mapOf(
                                "templateId" to 1,
                                "quizId" to qid
                            )
                            quizDoc.reference.set(update, merge = true)
                            quizzesUpdated++
                            if (quizzesUpdated % 50 == 0) {
                                onLog("[Admin] Progress: users=$usersProcessed, visited=$quizzesVisited, updated=$quizzesUpdated")
                            }
                        } catch (perQuizError: Throwable) {
                            failures++
                            val msg =
                                "[Admin] Failed updating quiz $qid for user ${userDoc.id}: ${perQuizError.message}"
                            Logger.error(msg)
                            onLog(msg)
                        }
                    }
                } catch (perUserError: Throwable) {
                    failures++
                    val msg = "[Admin] Failed reading quizzes for user ${userDoc.id}: ${perUserError.message}"
                    Logger.error(msg)
                    onLog(msg)
                }
            }
        } catch (e: Throwable) {
            failures++
            val msg = "[Admin] Failed iterating users for backfill: ${e.message}"
            Logger.error(msg)
            onLog(msg)
        }

        val summary =
            "Users=$usersProcessed, QuizzesVisited=$quizzesVisited, QuizzesUpdated=$quizzesUpdated, Failures=$failures"
        onLog("[Admin] Backfill summary: $summary")
        onLog("[Admin] Finished at ${currentTime()}")
        summary
    }

    /**
     * Processes all users in the users collection and creates email documents for those with valid emails and quizzes.
     * For each user with an email and at least one quiz, creates a document in the emails collection.
     *
     * @param onProgress Callback function to report progress logs
     * @return Summary string with processing results
     */
    suspend fun sendEmailsToPreGeneratedRecs(onProgress: (String) -> Unit): String = withContext(Dispatchers.Default) {
        try {
            onProgress("Starting to process all users for email generation...")

            val usersCollection = firestore.collection("users")
            val usersSnapshot = usersCollection.get()

            var totalUsers = 0
            var usersWithEmail = 0
            var usersWithQuizzes = 0
            var emailsCreated = 0
            var errors = 0

            onProgress("Found ${usersSnapshot.documents.size} users to process")

            for (userDoc in usersSnapshot.documents) {
                totalUsers++

                try {
                    val userId = userDoc.id
                    onProgress("Processing user: $userId")

                    // Check if user has email
                    val email = if (userDoc.contains("email")) {
                        userDoc.get("email") as? String
                    } else null

                    if (email.isNullOrBlank()) {
                        onProgress("User $userId has no email, skipping")
                        continue
                    }

                    usersWithEmail++
                    onProgress("User $userId has email: $email")

                    // Get quizzes collection for this user
                    val quizzesSnapshot = userDoc.reference.collection("quizzes").get()

                    if (quizzesSnapshot.documents.isEmpty()) {
                        onProgress("User $userId has no quizzes, skipping")
                        continue
                    }

                    usersWithQuizzes++
                    val firstQuizId = quizzesSnapshot.documents.first().id
                    onProgress("User $userId has ${quizzesSnapshot.documents.size} quizzes, first quiz ID: $firstQuizId")

                    // Get user's first name from the user document or use default
                    val firstName = "Viitor Dr."

                    // Create email document in emails collection
                    val emailsCollection = firestore.collection("emails")
                    emailsCollection.add(
                        mapOf(
                            "to" to email,
                            "message" to mapOf(
                                "subject" to "Recomandările tale de specialități la Rezi sunt gata! 🎉",
                                "html" to EmailTemplates.RECOMMENDATIONS_READY
                                    .replace("{name}", firstName)
                                    .replace("{link}", "https://rezident.info/recomandari?quizId=$firstQuizId")
                            ),
                        )
                    )

                    emailsCreated++
                    onProgress("Created email document for $email (user: $userId)")
                } catch (e: Exception) {
                    errors++
                    onProgress("Error processing user ${userDoc.id}: ${e.message}")
                    Logger.error("Error processing user ${userDoc.id}: ${e.message}")
                }
            }

            val summary = "Processing completed. Total users: $totalUsers, " +
                    "Users with email: $usersWithEmail, " +
                    "Users with quizzes: $usersWithQuizzes, " +
                    "Emails created: $emailsCreated, " +
                    "Errors: $errors"

            onProgress(summary)
            summary

        } catch (e: Exception) {
            val errorMsg = "Error processing users for emails: ${e.message}"
            onProgress(errorMsg)
            Logger.error(errorMsg)
            errorMsg
        }
    }

    /**
     * Sends emails to waitlist users who are not in the users collection using the RECOMMENDATIONS_READY_NO_QUIZ template.
     * Takes a list of waitlist-only emails and creates email documents for each one.
     *
     * @param waitlistOnlyEmails List of email addresses that are in waitlist but not in users collection
     * @param onProgress Callback function to report progress logs
     * @return Summary string with processing results
     */
    suspend fun sendEmailsToWaitlistOnlyUsers(
        waitlistOnlyEmails: List<String>,
        onProgress: (String) -> Unit
    ): String = withContext(Dispatchers.Default) {
        try {
            onProgress("Starting to send emails to ${waitlistOnlyEmails.size} waitlist-only users...")

            var emailsCreated = 0
            var errors = 0

            for (email in waitlistOnlyEmails) {
                try {
                    onProgress("Processing email: $email")

                    // Get user's first name from the email or use default
                    val firstName = "Viitor Dr."

                    // Create email document in emails collection
                    val emailsCollection = firestore.collection("emails")
                    emailsCollection.add(
                        mapOf(
                            "to" to email,
                            "message" to mapOf(
                                "subject" to "Recomandările tale de specialități la Rezi sunt gata! 🎉",
                                "html" to EmailTemplates.RECOMMENDATIONS_READY_NO_QUIZ
                                    .replace("{name}", firstName)
                            ),
                        )
                    )

                    emailsCreated++
                    onProgress("Created email document for $email")
                } catch (e: Exception) {
                    errors++
                    onProgress("Error processing email $email: ${e.message}")
                    Logger.error("Error processing email $email: ${e.message}")
                }
            }

            val summary = "Processing completed. Total waitlist-only emails: ${waitlistOnlyEmails.size}, " +
                    "Emails created: $emailsCreated, " +
                    "Errors: $errors"

            onProgress(summary)
            summary

        } catch (e: Exception) {
            val errorMsg = "Error processing waitlist-only emails: ${e.message}"
            onProgress(errorMsg)
            Logger.error(errorMsg)
            errorMsg
        }
    }

    /**
     * One-off script to create Quiz Template document with id "2" in /quiz/{id}
     * Creates the template document along with steps and questions subcollections.
     *
     * @param onProgress Callback function to report progress logs
     * @return Summary string with results
     */
    suspend fun createQuizTemplate2(
        onProgress: (String) -> Unit
    ): String = withContext(Dispatchers.Default) {
        try {
            onProgress("Starting to create Quiz Template 2...")

            val quizTemplateRef = firestore.collection("quiz").document("2")

            // Create main quiz template document
            onProgress("Creating main template document...")
            quizTemplateRef.set(
                mapOf(
                    "id" to 2,
                    "createdAt" to currentTime(),
                    "name" to "Quiz de Orientare"
                )
            )
            onProgress("Main template document created")

            // Create steps subcollection
            onProgress("Creating steps subcollection...")
            val stepsData = listOf(
                mapOf(
                    "id" to 1,
                    "title" to "Cine ești",
                    "subtitle" to "Acest domeniu surprinde trăsăturile dumneavoastră stabile, dispoziționale, care sunt esențiale pentru Potrivirea Persoană-Mediu (P-E Fit). Trăsături precum Conștiinciozitatea și Stabilitatea Emoțională pot fi predictori statistic ai succesului academic la Examenul Național de Rezidențiat, funcționând ca un filtru puternic pentru specialitățile competitive."
                ),
                mapOf(
                    "id" to 2,
                    "title" to "La ce te pricepi",
                    "subtitle" to "Aceste întrebări evaluează competențele cognitive și tehnice specifice cerute de diferite sarcini medicale. Măsoară abilități precum Dexteritatea Manuală Fină, Raționamentul Spațial și Rezolvarea Analitică/Logică de Probleme. Corelarea aptitudinilor dumneavoastră cu cerințele tehnice ale unei specialități este crucială pentru performanță și satisfacția pe termen lung."
                ),
                mapOf(
                    "id" to 3,
                    "title" to "Ce îți dorești",
                    "subtitle" to "Acest segment cuantifică motivațiile și preferințele primare pentru contextul muncii, deoarece \"stilul de viață controlabil\" a devenit un factor puternic care influențează alegerea specialității în rândul absolvenților. Alinierea acestor valori (ex. ritm și echilibrul muncă-viață) este esențială pentru satisfacția zilnică și prevenirea burnout-ului."
                )
            )

            for (stepData in stepsData) {
                val stepId = stepData["id"] as Int
                quizTemplateRef.collection("steps").document(stepId.toString()).set(stepData)
                onProgress("Created step $stepId")
            }

            // Create questions subcollection
            onProgress("Creating questions subcollection...")
            val questionsData = listOf(
                // Step 1: Secțiunea A: Profil de Personalitate
                mapOf(
                    "id" to 1,
                    "mandatory" to true,
                    "question" to "Mă simt plin(ă) de energie și confortabil(ă) când interacționez cu grupuri mari de oameni.",
                    "factor" to "Extraversie",
                    "scaleExplanation" to "1 = Dezacord Total, 5 = Acord Total",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 1
                ),
                mapOf(
                    "id" to 2,
                    "mandatory" to true,
                    "question" to "Sunt adesea cel/cea care inițiază conversații într-un cadru social.",
                    "factor" to "Extraversie",
                    "scaleExplanation" to "1 = Dezacord Total, 5 = Acord Total",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 1
                ),
                mapOf(
                    "id" to 3,
                    "mandatory" to true,
                    "question" to "Sunt profund interesat(ă) de problemele altor persoane și simt o empatie puternică pentru ele.",
                    "factor" to "Agreabilitate",
                    "scaleExplanation" to "1 = Dezacord Total, 5 = Acord Total",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 1
                ),
                mapOf(
                    "id" to 4,
                    "mandatory" to true,
                    "question" to "Sunt o persoană de agreabilă și îmi este ușor să cooperez cu ceilalți.",
                    "factor" to "Agreabilitate",
                    "scaleExplanation" to "1 = Dezacord Total, 5 = Acord Total",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 1
                ),
                mapOf(
                    "id" to 5,
                    "mandatory" to true,
                    "question" to "Sunt o persoană foarte organizată, care acordă o atenție deosebită detaliilor.",
                    "factor" to "Conștiinciozitate",
                    "scaleExplanation" to "1 = Dezacord Total, 5 = Acord Total",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 1
                ),
                mapOf(
                    "id" to 6,
                    "mandatory" to true,
                    "question" to "Sunt întotdeauna pregătit(ă) și conștiincios/conștiincioasă în îndeplinirea îndatoririlor mele.",
                    "factor" to "Conștiinciozitate",
                    "scaleExplanation" to "1 = Dezacord Total, 5 = Acord Total",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 1
                ),
                mapOf(
                    "id" to 7,
                    "mandatory" to true,
                    "question" to "Rămân calm(ă) și stăpânit(ă) în situații tensionate, de mare presiune.",
                    "factor" to "Stabilitate Emoțională",
                    "scaleExplanation" to "1 = Dezacord Total, 5 = Acord Total",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 1
                ),
                mapOf(
                    "id" to 8,
                    "mandatory" to true,
                    "question" to "În general, sunt relaxat(ă) și nu îmi fac griji excesive în legătură cu lucrurile.",
                    "factor" to "Stabilitate Emoțională",
                    "scaleExplanation" to "1 = Dezacord Total, 5 = Acord Total",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 1
                ),
                mapOf(
                    "id" to 9,
                    "mandatory" to true,
                    "question" to "Am o imaginație bogată și îmi place să explorez idei abstracte și teorii complexe.",
                    "factor" to "Deschidere",
                    "scaleExplanation" to "1 = Dezacord Total, 5 = Acord Total",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 1
                ),
                mapOf(
                    "id" to 10,
                    "mandatory" to true,
                    "question" to "Sunt curios/curioasă în legătură cu multe lucruri diverse și deschis(ă) la experiențe noi și neconvenționale.",
                    "factor" to "Deschidere",
                    "scaleExplanation" to "1 = Dezacord Total, 5 = Acord Total",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 1
                ),
                mapOf(
                    "id" to 11,
                    "mandatory" to true,
                    "question" to "În ce an de facultate ești?",
                    "single" to true,
                    "answers" to mapOf(
                        "1" to "Încă nu am început facultatea",
                        "2" to "1",
                        "3" to "2",
                        "4" to "3",
                        "5" to "4",
                        "6" to "5",
                        "7" to "6",
                        "8" to "Sunt deja la Rezidențiat"
                    ),
                    "stepId" to 1
                ),
                // Step 2: Secțiunea B: Profil de Abilități și Aptitudini
                mapOf(
                    "id" to 12,
                    "mandatory" to true,
                    "question" to "Abilitatea mea de a efectua sarcini precise, delicate, cu mâinile (de ex., desen în detaliu, cântat la un instrument, reparații migăloase).",
                    "factor" to "Dexteritate Manuală",
                    "scaleExplanation" to "1 = Începător, 5 = Expert",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 2
                ),
                mapOf(
                    "id" to 13,
                    "mandatory" to true,
                    "question" to "Abilitatea mea de a vizualiza mental și de a manipula obiecte tridimensionale și structuri anatomice complexe.",
                    "factor" to "Vizualizare Structurală",
                    "scaleExplanation" to "1 = Începător, 5 = Expert",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 2
                ),
                mapOf(
                    "id" to 14,
                    "mandatory" to true,
                    "question" to "Abilitatea mea de a sintetiza informații disparate (simptome, rezultate de laborator, istoric medical) pentru a formula o concluzie coerentă sau un diagnostic.",
                    "factor" to "Raționament Inductiv",
                    "scaleExplanation" to "1 = Începător, 5 = Expert",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 2
                ),
                mapOf(
                    "id" to 15,
                    "mandatory" to true,
                    "question" to "Abilitatea mea de a observa detalii vizuale subtile și tipare în imagini, lame (microscopice) sau seturi de date.",
                    "factor" to "Atenție la Detaliu",
                    "scaleExplanation" to "1 = Începător, 5 = Expert",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 2
                ),
                mapOf(
                    "id" to 16,
                    "mandatory" to true,
                    "question" to "Abilitatea mea de a înțelege și de a împărtăși sentimentele altora și de a explica informații complexe în mod clar și plin de compasiune.",
                    "factor" to "Empatie și Comunicare",
                    "scaleExplanation" to "1 = Începător, 5 = Expert",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 2
                ),
                mapOf(
                    "id" to 17,
                    "mandatory" to true,
                    "question" to "Abilitatea mea de a conduce o echipă, de a coordona sarcini și de a colabora eficient cu colegii.",
                    "factor" to "Leadership și Muncă în Echipă",
                    "scaleExplanation" to "1 = Începător, 5 = Expert",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 2
                ),
                // Step 3: Secțiunea C: Preferințe privind Munca și Stilul de Viață
                mapOf(
                    "id" to 18,
                    "mandatory" to true,
                    "question" to "Ce fel de specialitate preferi?",
                    "single" to true,
                    "answers" to mapOf(
                        "1" to "Chirurgicală",
                        "2" to "Non-chirurgicală",
                        "3" to "Sunt ok ambele variante"
                    ),
                    "stepId" to 3
                ),
                mapOf(
                    "id" to 19,
                    "mandatory" to true,
                    "question" to "Cât de important este să aveți un program de lucru predictibil, cu gărzi minime și timp suficient pentru viața personală?",
                    "factor" to "Echilibru Muncă-Viață Personală",
                    "scaleExplanation" to "1 = Neimportant, 5 = Importanță Critică",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 3
                ),
                mapOf(
                    "id" to 20,
                    "mandatory" to true,
                    "question" to "Mă dezvolt și prefer mediile de lucru cu presiune ridicată și ritm alert.",
                    "factor" to "Mediu cu Stres Ridicat",
                    "scaleExplanation" to "1 = Dezacord Total, 5 = Acord Total",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 3
                ),
                mapOf(
                    "id" to 21,
                    "mandatory" to true,
                    "question" to "Durata preferată pentru îngrijirea pacienților",
                    "factor" to "Tipul de Relație cu Pacientul",
                    "scaleExplanation" to "1 = Interacțiuni scurte, focalizate, de rezolvare a problemelor, cu pacienți noi; 5 = Îngrijire continuă, pe termen lung, cu aceiași pacienți",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 3
                ),
                mapOf(
                    "id" to 22,
                    "mandatory" to true,
                    "question" to "Care este mediul dvs. de lucru principal preferat?",
                    "factor" to "Mediu de Lucru Principal",
                    "scaleExplanation" to "1 = Cabinet privat / ambulatoriu; 5 = Spital mare / centru universitar",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 3
                ),
                mapOf(
                    "id" to 23,
                    "mandatory" to true,
                    "question" to "Cât de important este potențialul ridicat de câștig viitor în alegerea carierei dvs.?",
                    "factor" to "Potențial de Venit",
                    "scaleExplanation" to "1 = Neimportant, 5 = Importanță Critică",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 3
                ),
                mapOf(
                    "id" to 24,
                    "mandatory" to true,
                    "question" to "Cât de important este ca specialitatea dvs. să includă oportunități semnificative pentru cercetare științifică și activități academice?",
                    "factor" to "Orientare către Cercetare",
                    "scaleExplanation" to "1 = Neimportant, 5 = Importanță Critică",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 3
                ),
                mapOf(
                    "id" to 25,
                    "mandatory" to true,
                    "question" to "Cât de mult preferați o muncă ce este în mare parte procedurală și practică (\"hands-on\")?",
                    "factor" to "Muncă Procedurală",
                    "scaleExplanation" to "1 = Nu îmi place deloc, 5 = Prefer în mod deosebit",
                    "single" to true,
                    "fiveScale" to true,
                    "stepId" to 3
                )
            )

            var questionCount = 0
            for (questionData in questionsData) {
                val questionId = questionData["id"] as Int
                quizTemplateRef.collection("questions").document(questionId.toString()).set(questionData)
                questionCount++
                if (questionCount % 5 == 0) {
                    onProgress("Created $questionCount questions...")
                }
            }

            val summary = "Successfully created Quiz Template 2 with 3 steps and 25 questions"
            onProgress(summary)
            summary

        } catch (e: Exception) {
            val errorMsg = "Error creating Quiz Template 2: ${e.message}"
            onProgress(errorMsg)
            Logger.error(errorMsg)
            errorMsg
        }
    }

    /**
     * One-off script to create Quiz Template document with id "3" in /quiz/{id}
     * from the 2026 matching specialties CSV. Creates the template document, steps,
     * and questions subcollection.
     *
     * @param onProgress Callback function to report progress logs
     * @return Summary string with results
     */
    suspend fun createQuizTemplate3FromMatchingSpecialtiesCsv(
        onProgress: (String) -> Unit
    ): String = withContext(Dispatchers.Default) {
        try {
            onProgress("Starting to create Quiz Template 3 from matching specialties CSV...")

            val quizTemplateRef = firestore.collection("quiz").document(MATCHING_SPECIALTIES_TEMPLATE_ID.toString())
            val questions = matchingSpecialtiesCsvQuestions()

            require(questions.size == 30) {
                "Expected 30 questions, found ${questions.size}"
            }
            require(questions.map { it.id } == (1..30).toList()) {
                "Expected question ids 1..30"
            }

            onProgress("Creating main template document at /quiz/$MATCHING_SPECIALTIES_TEMPLATE_ID...")
            quizTemplateRef.set(
                mapOf(
                    "id" to MATCHING_SPECIALTIES_TEMPLATE_ID,
                    "createdAt" to currentTime(),
                    "name" to "Quiz de Orientare v3"
                )
            )
            onProgress("Main template document created")

            onProgress("Creating steps subcollection...")
            for (stepData in matchingSpecialtiesStepsData()) {
                val stepId = stepData["id"] as Int
                quizTemplateRef.collection("steps").document(stepId.toString()).set(stepData)
                onProgress("Created step $stepId")
            }

            onProgress("Creating questions subcollection at /quiz/$MATCHING_SPECIALTIES_TEMPLATE_ID/questions...")
            var questionCount = 0
            for (question in questions) {
                quizTemplateRef.collection("questions").document(question.id.toString()).set(question)
                questionCount++
                if (questionCount % 5 == 0) {
                    onProgress("Created $questionCount questions...")
                }
            }

            val summary = "Successfully created Quiz Template 3 with 3 steps and ${questions.size} questions"
            onProgress(summary)
            summary
        } catch (e: Exception) {
            val errorMsg = "Error creating Quiz Template 3 from matching specialties CSV: ${e.message}"
            onProgress(errorMsg)
            Logger.error(errorMsg)
            errorMsg
        }
    }

    private const val MATCHING_SPECIALTIES_TEMPLATE_ID = 3

    private val matchingSpecialtiesMultiSelectQuestionIds = setOf(27, 29)
    private val matchingSpecialtiesHardcodedAnswers = mapOf(
        27 to listOf("Exclud Specialitățile Clinice Medicale", "Exclud Specialitățile Clinice Chirurgicale", "Exclud Specialitățile Paraclinice", "Nu"),
        28 to listOf("Da", "Nu"),
        29 to listOf("Exclud Specialitățile Pediatrice", "Exclud Geriatrie & Gerontologie", "Nu"),
        30 to listOf("Încă nu am început facultatea", "1", "2", "3", "4", "5", "6", "Sunt deja medic")
    )

    private fun matchingSpecialtiesStepsData(): List<Map<String, Any>> {
        return listOf(
            mapOf(
                "id" to 1,
                "title" to "Personalitate",
                "subtitle" to "Aceste întrebări evaluează trăsăturile tale de bază prin modelul psihologic Big Five (OCEAN). Răspunsurile ne ajută să înțelegem cum abordezi interacțiunile sociale, rigoarea, stresul și ideile noi pentru a contura un profil profesional individualizat."
            ),
            mapOf(
                "id" to 2,
                "title" to "Skill-uri",
                "subtitle" to "Aceste întrebări evaluează competențele cognitive și tehnice cerute de diferite specialități. Corelarea skill-urilor tale cu cerințele tehnice ale specialităților este crucială pentru performanța și satisfacția pe termen lung."
            ),
            mapOf(
                "id" to 3,
                "title" to "Preferințe",
                "subtitle" to "Aceste întrebări ajută să potrivim profilul tău cu specialități care îți respectă așteptările privind echilibrul muncă-viață personală, ritmul de lucru, gradul de interacțiune cu pacienții și obiectivele de carieră."
            )
        )
    }

    private fun matchingSpecialtiesCsvQuestions(): List<QuizQuestionModel> {
        return listOf(
            matchingSpecialtiesQuestion(
                id = 1,
                factor = "Deschidere",
                question = "Am o imaginație bogată și îmi place să explorez idei abstracte și teorii complexe.",
                scaleExplanation = "1 = Dezacord Total, 5 = Acord Total"
            ),
            matchingSpecialtiesQuestion(
                id = 2,
                factor = "Deschidere",
                question = "Sunt curios/curioasă în legătură cu multe lucruri diferite și deschis(ă) la experiențe noi și neconvenționale.",
                scaleExplanation = "1 = Dezacord Total, 5 = Acord Total"
            ),
            matchingSpecialtiesQuestion(
                id = 3,
                factor = "Conștiinciozitate",
                question = "Sunt o persoană foarte organizată.",
                scaleExplanation = "1 = Dezacord Total, 5 = Acord Total"
            ),
            matchingSpecialtiesQuestion(
                id = 4,
                factor = "Conștiinciozitate",
                question = "Sunt întotdeauna conștiincios/conștiincioasă în îndeplinirea îndatoririlor mele.",
                scaleExplanation = "1 = Dezacord Total, 5 = Acord Total"
            ),
            matchingSpecialtiesQuestion(
                id = 5,
                factor = "Extraversie",
                question = "Mă simt plin(ă) de energie și confortabil(ă) când interacționez cu grupuri mari de oameni.",
                scaleExplanation = "1 = Dezacord Total, 5 = Acord Total"
            ),
            matchingSpecialtiesQuestion(
                id = 6,
                factor = "Extraversie",
                question = "Sunt adesea cel/cea care inițiază conversații într-un cadru social.",
                scaleExplanation = "1 = Dezacord Total, 5 = Acord Total"
            ),
            matchingSpecialtiesQuestion(
                id = 7,
                factor = "Agreabilitate",
                question = "Sunt adesea dispus(ă) să îmi las propriile interese pe plan secund pentru a-i ajuta pe ceilalți.",
                scaleExplanation = "1 = Dezacord Total, 5 = Acord Total"
            ),
            matchingSpecialtiesQuestion(
                id = 8,
                factor = "Agreabilitate",
                question = "Tind adesea să văd binele în oameni.",
                scaleExplanation = "1 = Dezacord Total, 5 = Acord Total"
            ),
            matchingSpecialtiesQuestion(
                id = 9,
                factor = "Stabilitate Emoțională",
                question = "Rămân calm(ă) și stăpânit(ă) în situații tensionate, de mare presiune.",
                scaleExplanation = "1 = Dezacord Total, 5 = Acord Total"
            ),
            matchingSpecialtiesQuestion(
                id = 10,
                factor = "Stabilitate Emoțională",
                question = "În general, sunt relaxat(ă) și nu îmi fac griji excesive în legătură cu lucrurile.",
                scaleExplanation = "1 = Dezacord Total, 5 = Acord Total"
            ),
            matchingSpecialtiesQuestion(
                id = 11,
                factor = "Dexteritate Manuală",
                question = "Abilitatea mea de a efectua sarcini precise, delicate, cu mâinile (de ex., desen de detaliu, cântat la un instrument, reparații migăloase).",
                scaleExplanation = "1 = Începător, 5 = Expert"
            ),
            matchingSpecialtiesQuestion(
                id = 12,
                factor = "Vizualizare Spațială",
                question = "Abilitatea mea de a îmi imagina și de a vizualiza mental obiecte tridimensionale și structuri anatomice complexe.",
                scaleExplanation = "1 = Începător, 5 = Expert"
            ),
            matchingSpecialtiesQuestion(
                id = 13,
                factor = "Raționament Inductiv",
                question = "Abilitatea mea de a sintetiza informații disparate (simptome, rezultate de laborator, istoric medical) pentru a formula o concluzie coerentă sau un diagnostic.",
                scaleExplanation = "1 = Începător, 5 = Expert"
            ),
            matchingSpecialtiesQuestion(
                id = 14,
                factor = "Atenția la Detalii",
                question = "Abilitatea mea de a observa detalii vizuale subtile și tipare în imagini, sau la microscop, sau în seturi de date.",
                scaleExplanation = "1 = Începător, 5 = Expert"
            ),
            matchingSpecialtiesQuestion(
                id = 15,
                factor = "Empatie",
                question = "Abilitatea mea de a înțelege și de a împărtăși sentimentele altora.",
                scaleExplanation = "1 = Începător, 5 = Expert"
            ),
            matchingSpecialtiesQuestion(
                id = 16,
                factor = "Comunicare",
                question = "Abilitatea mea de a explica informații complexe în mod clar și plin de compasiune.",
                scaleExplanation = "1 = Începător, 5 = Expert"
            ),
            matchingSpecialtiesQuestion(
                id = 17,
                factor = "Muncă în Echipă",
                question = "Abilitatea mea de a colabora eficient cu colegii.",
                scaleExplanation = "1 = Începător, 5 = Expert"
            ),
            matchingSpecialtiesQuestion(
                id = 18,
                factor = "Leadership",
                question = "Abilitatea mea de a conduce o echipă și de a coordona sarcini.",
                scaleExplanation = "1 = Începător, 5 = Expert"
            ),
            matchingSpecialtiesQuestion(
                id = 19,
                factor = "Tolerabilitate Stres Ridicat",
                question = "Mă dezvolt și prefer mediile de lucru cu presiune ridicată și ritm alert.",
                scaleExplanation = "1 = Dezacord Total, 5 = Acord Total"
            ),
            matchingSpecialtiesQuestion(
                id = 20,
                factor = "Durata Îngrijirii Pacientului",
                question = "Ce durată a îngrijirii pacientului preferi?",
                scaleExplanation = "1 = Interacțiuni scurte, focalizate, de rezolvare a problemelor, cu pacienți noi; 5 = Îngrijire continuă, pe termen lung, cu aceiași pacienți"
            ),
            matchingSpecialtiesQuestion(
                id = 21,
                factor = "Mediu de Lucru",
                question = "Preferi să lucrezi mai independent sau mai mult în echipă?",
                scaleExplanation = "1 = Independent, 5 = Echipă"
            ),
            matchingSpecialtiesQuestion(
                id = 22,
                factor = "Potențial de Venit",
                question = "Cât de important este potențialul ridicat de câștig viitor în alegerea carierei tale?",
                scaleExplanation = "1 = Neimportant, 5 = Importanță Critică"
            ),
            matchingSpecialtiesQuestion(
                id = 23,
                factor = "Orientare către Cercetare",
                question = "Cât de important este ca specialitatea ta să includă oportunități semnificative pentru cercetare științifică și activități academice?",
                scaleExplanation = "1 = Neimportant, 5 = Importanță Critică"
            ),
            matchingSpecialtiesQuestion(
                id = 24,
                factor = "Muncă Procedurală",
                question = "Cât de mult preferi o muncă ce este în mare parte procedurală și practică (\"hands-on\")?",
                scaleExplanation = "1 = Nu îmi place deloc, 5 = Prefer în mod deosebit"
            ),
            matchingSpecialtiesQuestion(
                id = 25,
                factor = "Echilibru Muncă-Viață Personală",
                question = "Cât de important este să ai un program de lucru predictibil, cu gărzi minime și timp suficient pentru viața personală?",
                scaleExplanation = "1 = Neimportant, 5 = Importanță Critică"
            ),
            matchingSpecialtiesQuestion(
                id = 26,
                factor = "Contact cu Pacienții",
                question = "Cât de mult contact preferi să ai cu pacienții?",
                scaleExplanation = "1 = Puțin, 5 = Mult"
            ),
            matchingSpecialtiesQuestion(
                id = 27,
                factor = "Categoria Specialității",
                question = "Dorești să EXCLUZI vreo categorie de specialități? (Maxim 2)",
            ),
            matchingSpecialtiesQuestion(
                id = 28,
                factor = "Expunerea la Decese",
                question = "Dorești să EXCLUZI specialitățile la care frecvența deceselor pacienților este mai mare?",
                scaleExplanation = "Specialități cu frecvență mai mare: Anestezie & Terapie Intensivă, Medicină de Urgență, Oncologie Medicală, Oncologie & Hematologie Pediatrică, Radioterapie, Chirurgie Cardiovasculară, Neurochirurgie, Anatomie Patologică, Medicină Legală"
            ),
            matchingSpecialtiesQuestion(
                id = 29,
                factor = "Grupe de Vârstă",
                question = "Dorești să EXCLUZI specialitățile care lucrează cu o anumită grupă de vârstă a pacienților?",
                scaleExplanation = "Specialități Pediatrice: Cardiologie Pediatrică, Gastroenterologie Pediatrică, Nefrologie Pediatrică, Neurologie Pediatrică, Oncologie & Hematologie Pediatrică, Pneumologie Pediatrică, Psihiatrie Pediatrică, Chirurgie Pediatrică, Ortopedie Pediatrică, Pediatrie, Neonatologie"
            ),
            matchingSpecialtiesQuestion(
                id = 30,
                factor = "An Universitar",
                question = "În ce an universitar ești?"
            )
        )
    }

    private fun matchingSpecialtiesQuestion(
        id: Int,
        factor: String,
        question: String,
        scaleExplanation: String? = null
    ): QuizQuestionModel {
        val hardcodedAnswers = matchingSpecialtiesHardcodedAnswers[id]

        return QuizQuestionModel(
            id = id,
            answers = if (hardcodedAnswers != null) {
                hardcodedAnswers.toPredefinedAnswers()
            } else {
                emptyMap()
            },
            mandatory = true,
            question = question,
            search = false,
            single = id !in matchingSpecialtiesMultiSelectQuestionIds,
            stepId = matchingSpecialtiesStepId(id),
            factor = factor,
            fiveScale = id in 1..26,
            scaleExplanation = scaleExplanation
        )
    }

    private fun matchingSpecialtiesStepId(questionId: Int): Int {
        return when (questionId) {
            in 1..10 -> 1
            in 11..19 -> 2
            in 20..30 -> 3
            else -> error("Unsupported matching specialties question id: $questionId")
        }
    }

    private fun List<String>.toPredefinedAnswers(): Map<Int, String> {
        return map { answer -> answer.trim() }
            .filter { answer -> answer.isNotEmpty() }
            .mapIndexed { index, answer -> (index + 1) to answer }
            .toMap()
    }
}
